import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Container,
  Button,
  Typography,
  Alert,
  IconButton,
  InputAdornment,
  TextField,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { getStaticFileUrl } from '../utils/staticUtils';
import useAuthPageTheme from '../hooks/useAuthPageTheme';
import MobileAuthShell from '../components/auth/MobileAuthShell';
import AuthMobileCard from '../components/auth/AuthMobileCard';
import AuthDarkTextField from '../components/auth/AuthDarkTextField';
import AuthBrandingPanel from '../components/auth/AuthBrandingPanel';
import { authFormPanelSxWithGlow } from '../components/auth/authLightPanelTheme';
import '../components/auth/authPages.css';
import {
  AUTH_COPYRIGHT,
  authCopyrightSx,
  darkPrimaryButtonSx,
  darkAlertSx,
  darkSuccessAlertSx,
} from '../components/auth/loginDarkTheme';
import './ForgotPasswordPage.css';

/**
 * Forgot Password Page Component
 * Allows users to request a password reset via email
 */
const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const { isCompact, formPanelThemeClass } = useAuthPageTheme();
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Get CSRF token from cookie
  const getCsrfToken = () => {
    const cookies = document.cookie.split(';');
    const csrfCookie = cookies.find(c => c.trim().startsWith('csrftoken='));
    return csrfCookie ? csrfCookie.split('=')[1] : '';
  };

  // Validate email format
  const validateEmail = (value) => {
    if (!value || !value.trim()) {
      return 'Email is required';
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value.trim())) {
      return 'Please enter a valid email address';
    }
    return null;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validateEmail(email);
    if (validationError) {
      setErrors({ email: [validationError] });
      return;
    }
    
    setIsSubmitting(true);
    setErrors({});
    setSuccessMessage('');

    try {
      const formData = new FormData();
      formData.append('email', email.trim());

      const response = await axios.post('/password-reset/', formData, {
        headers: {
          'X-CSRFToken': getCsrfToken(),
        },
        withCredentials: true,
      });

      if (response.data.success) {
        setSuccessMessage(response.data.message || 'If an account with that email exists, we have sent a password reset link.');
        setEmail('');
      } else {
        setErrors(response.data.errors || { non_field_errors: ['An error occurred. Please try again.'] });
      }
    } catch (error) {
      if (error.response && error.response.data) {
        if (error.response.data.errors) {
          setErrors(error.response.data.errors);
        } else {
          setErrors({ non_field_errors: ['An error occurred. Please try again.'] });
        }
      } else {
        setErrors({ non_field_errors: ['An error occurred. Please try again.'] });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isCompact) {
    return (
      <MobileAuthShell
        footer={
          <Typography variant="caption" sx={authCopyrightSx}>
            {AUTH_COPYRIGHT}
          </Typography>
        }
      >
        <AuthMobileCard
          title="Forgot your password?"
          subtitle="We will send you a link to create a new password."
          onBack={() => navigate('/login/')}
          backDisabled={isSubmitting}
        >
          {successMessage && (
            <Alert severity="success" sx={darkSuccessAlertSx}>
              {successMessage}
            </Alert>
          )}
          {errors.non_field_errors && (
            <Alert severity="error" sx={darkAlertSx}>
              {Array.isArray(errors.non_field_errors)
                ? errors.non_field_errors.map((error, index) => <div key={index}>{error}</div>)
                : errors.non_field_errors}
            </Alert>
          )}
          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: '100%' }}>
            <AuthDarkTextField
              type="email"
              label="Email"
              name="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setErrors({});
                setSuccessMessage('');
              }}
              autoFocus
              error={!!errors.email}
              helperText={
                errors.email ? (Array.isArray(errors.email) ? errors.email.join(', ') : errors.email) : ''
              }
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isSubmitting}
              sx={darkPrimaryButtonSx}
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </Button>
          </Box>
        </AuthMobileCard>
      </MobileAuthShell>
    );
  }

  return (
    <Box
      className="forgot-password-page"
      sx={{
        display: 'flex',
        minHeight: '100vh',
        width: '100%',
        backgroundColor: '#ffffff',
      }}
    >
      <AuthBrandingPanel />

      {/* Right Panel - Forgot Password Form */}
      <Box
        className={`login-form-panel ${formPanelThemeClass}`}
        sx={{
          ...authFormPanelSxWithGlow,
          padding: { xs: 3, sm: 4, md: 6, lg: 8 },
        }}
      >
        <Container
          maxWidth="sm"
          sx={{
            width: '100%',
            px: { xs: 0, sm: 2 },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            flex: 1,
            position: 'relative',
            zIndex: 1,
            '@media (min-width: 600px)': {
              maxWidth: '420px',
            },
          }}
        >
          <Box
            sx={{
              animation: 'fadeInUp 0.5s ease-out',
              '@keyframes fadeInUp': {
                '0%': {
                  opacity: 0,
                  transform: 'translateY(20px)',
                },
                '100%': {
                  opacity: 1,
                  transform: 'translateY(0)',
                },
              },
            }}
          >
            {/* Logo */}
            <Box
              component="img"
              src={getStaticFileUrl('topbar/logo_ed.png')}
              alt="RoomNexa"
              sx={{
                height: '48px',
                width: 'auto',
                mb: 4,
                mx: 'auto',
                display: 'block',
                filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.08))',
              }}
            />

            {/* Title */}
            <Typography
              component="span"
              className="auth-form-title"
              sx={{
                display: 'block',
                fontWeight: 800,
                mb: 1.5,
                color: '#111827',
                fontSize: { xs: '28px', sm: '32px', md: '36px' },
                textAlign: 'left',
                letterSpacing: '-0.02em',
              }}
            >
              Forgot your password?
            </Typography>

            <Typography
              component="span"
              className="auth-form-subtitle"
              sx={{
                display: 'block',
                mb: 4,
                color: '#374151',
                fontSize: '16px',
                textAlign: 'left',
                lineHeight: 1.6,
                fontWeight: 600,
              }}
            >
              We will send you a link to create new password
            </Typography>

            {/* Success Message */}
            {successMessage && (
              <Alert
                severity="success"
                sx={{
                  mb: 3,
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(76, 175, 80, 0.15)',
                }}
              >
                {successMessage}
              </Alert>
            )}

            {/* Error Messages */}
            {errors.non_field_errors && (
              <Alert
                severity="error"
                sx={{
                  mb: 3,
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(211, 47, 47, 0.15)',
                  '& .MuiAlert-icon': {
                    color: '#d32f2f',
                  },
                }}
              >
                {Array.isArray(errors.non_field_errors)
                  ? errors.non_field_errors.map((error, index) => <div key={index}>{error}</div>)
                  : errors.non_field_errors}
              </Alert>
            )}

          {/* Forgot Password Form */}
          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: '100%' }}>
            {/* Email Input */}
            <TextField
              fullWidth
              type="email"
              label="Email"
              name="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setErrors({});
                setSuccessMessage('');
              }}
              required={false}
              autoFocus
              error={!!errors.email}
              helperText={errors.email ? (Array.isArray(errors.email) ? errors.email.join(', ') : errors.email) : ''}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '0',
                  backgroundColor: '#fafafa',
                  fontSize: '16px',
                  height: '56px',
                  color: '#212121',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                    '& fieldset': {
                      borderColor: '#4ce1b6',
                      borderWidth: '2px',
                    },
                  },
                  '&.Mui-focused': {
                    backgroundColor: '#ffffff',
                    boxShadow: '0 0 0 4px rgba(76, 225, 182, 0.1)',
                    '& fieldset': {
                      borderColor: '#4ce1b6',
                      borderWidth: '2px',
                    },
                  },
                  '& fieldset': {
                    borderColor: '#e0e0e0',
                    borderWidth: '1px',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  },
                },
                '& .MuiInputLabel-root': {
                  fontSize: '15px',
                  color: '#757575',
                  '&.Mui-focused': {
                    color: '#4ce1b6',
                    fontWeight: 500,
                  },
                },
                '& .MuiInputBase-input': {
                  fontSize: '16px',
                  color: '#212121',
                  padding: '16.5px 16px',
                },
                '& .MuiFormHelperText-root': {
                  fontSize: '12px',
                  marginLeft: '4px',
                  marginTop: '6px',
                },
              }}
            />

            {/* Action Buttons */}
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                mb: 0,
              }}
            >
              {/* Back Button */}
              <Button
                type="button"
                variant="outlined"
                onClick={() => navigate('/login/')}
                sx={{
                  py: 1.75,
                  px: 3,
                  backgroundColor: '#ffffff',
                  color: '#212121',
                  borderColor: '#e0e0e0',
                  borderRadius: '0',
                  textTransform: 'none',
                  fontSize: '16px',
                  fontWeight: 600,
                  height: '52px',
                  flex: 1,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    backgroundColor: '#fafafa',
                    borderColor: '#bdbdbd',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
                    transform: 'translateY(-2px)',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                  },
                }}
              >
                Back
              </Button>

              {/* Submit Button */}
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting}
                sx={{
                  py: 1.75,
                  px: 3,
                  backgroundColor: '#000000',
                  color: '#ffffff',
                  borderRadius: '0',
                  textTransform: 'none',
                  fontSize: '16px',
                  fontWeight: 600,
                  height: '52px',
                  flex: 1,
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    backgroundColor: '#1a1a1a',
                    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.25)',
                    transform: 'translateY(-2px)',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                  },
                  '&:disabled': {
                    backgroundColor: '#bdbdbd',
                    color: '#ffffff',
                    transform: 'none',
                  },
                }}
              >
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </Button>
            </Box>
          </Box>
          </Box>

          {/* Copyright - Centered at bottom */}
          <Box
            sx={{
              mt: 4,
              pt: 3,
              textAlign: 'center',
              width: '100%',
              position: 'relative',
              zIndex: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: '#6b7280',
                fontSize: '12px',
                fontWeight: 600,
                letterSpacing: '0.3px',
              }}
            >
              © 2025 RoomNexa, Inc. Singapore · Worldwide
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default ForgotPasswordPage;

